try {
            if (process.env.TOKENS_FEATURE_FLAG === 'true') require('./index-new.css');
            else require('./index.css');
          } catch (e) {
            if (e.code !== 'MODULE_NOT_FOUND') {
              throw e; // Re-throw unexpected errors
            }
            console.warn('index.css not found, proceeding without it.');
          }
          

import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { CompositeDecorator, EditorState, convertFromRaw, Editor } from 'draft-js';
import { useRef, useState, forwardRef } from 'react';
import { Modal } from '@design-system/modal';
import { DangerouslySetInnerHtml } from '@phx-husky/dangerously-set-inner-html';
import { useQueryClient } from 'react-query';

const Image = ({ blockProps: { id, name, url } }) => (jsx("img", { "data-media-id": id, alt: name, src: url, className: "cds-rte-text__image" }));

var INLINE_BLOCK_TYPES;
(function (INLINE_BLOCK_TYPES) {
    INLINE_BLOCK_TYPES["LINK"] = "LINK";
    INLINE_BLOCK_TYPES["LEGAL_DOCUMENT"] = "LEGAL-DOCUMENT";
})(INLINE_BLOCK_TYPES || (INLINE_BLOCK_TYPES = {}));
var CUSTOM_BLOCK_TYPES;
(function (CUSTOM_BLOCK_TYPES) {
    CUSTOM_BLOCK_TYPES["IMAGE"] = "IMAGE";
})(CUSTOM_BLOCK_TYPES || (CUSTOM_BLOCK_TYPES = {}));
const CUSTOM_BLOCK_MAP = {
    [CUSTOM_BLOCK_TYPES.IMAGE]: {
        component: Image,
        editable: false,
    },
};

const LegalDocument = ({ entityKey, contentState }) => {
    const newButtonRef = useRef(null);
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const { legalDocumentId, legalDocumentIdLabel } = contentState
        .getEntity(entityKey)
        .getData();
    const data = queryClient.getQueryData(legalDocumentId);
    const close = () => {
        setShowModal(false);
        if (newButtonRef.current) {
            newButtonRef.current.focus();
        }
    };
    const handleLinkClick = (event) => {
        var _a;
        let element = event.target;
        while (element !== event.currentTarget) {
            if (element instanceof HTMLAnchorElement &&
                !((_a = element.getAttribute('href')) === null || _a === void 0 ? void 0 : _a.startsWith('#'))) {
                event.preventDefault();
                window.open(element.href, '_blank');
                return;
            }
            element = element.parentElement;
        }
    };
    return (jsxs(Fragment, { children: [jsx("button", Object.assign({ ref: newButtonRef, className: "cds-rte-text__legal-button", type: "button", onClick: () => setShowModal(true) }, { children: legalDocumentIdLabel })), jsx(Modal, Object.assign({ isShowing: showModal, closeModal: close, className: "cds-rte-text__legal-modal" }, { children: (data === null || data === void 0 ? void 0 : data.body) ? (jsx(DangerouslySetInnerHtml, { html: data.body, onClick: handleLinkClick })) : null }))] }));
};

const Link = ({ entityKey, contentState, children, }) => {
    const { url } = contentState.getEntity(entityKey).getData();
    return (jsx("a", Object.assign({ href: url, target: "_blank", rel: "noreferrer", className: "cds-rte-text__link" }, { children: children })));
};

const findEntitiesByType = (contentBlock, callback, contentState, type) => {
    contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return (entityKey !== null && contentState.getEntity(entityKey).getType() === type);
    }, callback);
};
const createDecorator = () => new CompositeDecorator([
    {
        strategy: (contentBlock, callback, contentState) => findEntitiesByType(contentBlock, callback, contentState, INLINE_BLOCK_TYPES.LINK),
        component: Link,
    },
    {
        strategy: (contentBlock, callback, contentState) => findEntitiesByType(contentBlock, callback, contentState, INLINE_BLOCK_TYPES.LEGAL_DOCUMENT),
        component: LegalDocument,
    },
]);
const getEntityForBlock = (block, state) => block.getEntityAt(0) && state.getEntity(block.getEntityAt(0));
const getCustomBlockToRender = (type, props) => CUSTOM_BLOCK_MAP[type] ? Object.assign(Object.assign({}, CUSTOM_BLOCK_MAP[type]), { props }) : null;
const blockRenderer = (contentBlock, editorState) => {
    const type = contentBlock.getType();
    const contentState = editorState.getCurrentContent();
    const entity = getEntityForBlock(contentBlock, contentState);
    if (type === 'atomic' && entity) {
        return getCustomBlockToRender(entity.getType(), entity.getData());
    }
};

const RTEText = forwardRef(({ className, text }, ref) => {
    const editorState = EditorState.createWithContent(convertFromRaw(JSON.parse(text)), createDecorator());
    return (jsx("div", Object.assign({ ref: ref, className: classNames('cds-rte-text', className) }, { children: jsx(Editor, { editorState: editorState, readOnly: true, blockRendererFn: (contentBlock) => blockRenderer(contentBlock, editorState) }) })));
});

export { RTEText };
